import { InputGroup } from "@smartsuite/react-ui/lib";
import type { PlainTextFieldProps } from "@smartsuite/react-ui/lib/components/forms/PlainTextField/PlainTextField";
import { MAX_PRECISION } from "@smartsuite/types";
import cx from "classnames";
import type React from "react";
import { useMemo } from "react";
import { NumericFormat } from "react-number-format";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import { Fields } from "../../../../types/fields";
import type { FormFieldProps } from "../../../types";
import type { TextFieldValue } from "../../text/text.config";
import { validateByType } from "../../text/validation";
import { getSeparators, isAllowedValue } from "./utils";

import "./NumberFieldControl.sass";

export type NumberFieldControlProps = FormFieldProps &
  Pick<PlainTextFieldProps, "placeholder" | "type">;

export const NumberFieldControl: React.FunctionComponent<NumberFieldControlProps> = ({
  caption,
  className,
  field,
  formItem,
  label,
  name,
  placeholder,
  required,
  type,
  readOnly,
}) => {
  const formField = useFormField<TextFieldValue>({
    name,
    validate: validateByType(type, field, formItem),
  });

  const inputClassName = cx("form-number-field", className, {
    "has-value": !!formField.value,
  });

  const separators = getSeparators(field.params);

  const fieldParameters = useMemo(
    () => ({
      max_length: field.params.max_length,
      allow_negative: field.params.allow_negative,
      decimal_separator: separators.decimal_separator,
      thousand_separator: separators.thousand_separator,
    }),
    [
      field.params.allow_negative,
      field.params.max_length,
      separators.decimal_separator,
      separators.thousand_separator,
    ],
  );

  const decimalScale = field.field_type === Fields.numbersliderfield ? 0 : MAX_PRECISION;

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      helpText={formItem.params.helpText}
      errorMessage={formField.errorMessage}
      label={label}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      <InputGroup
        className="form-number-field-group"
        inputBackground="white-new"
        state={formField.state}
      >
        <NumericFormat
          className={inputClassName}
          value={formField.value}
          onBlur={formField.onBlur}
          onValueChange={(values) => {
            formField.onChange(values.floatValue ?? "");
          }}
          onFocus={formField.onFocus}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          decimalScale={decimalScale}
          decimalSeparator={fieldParameters.decimal_separator}
          thousandSeparator={fieldParameters.thousand_separator}
          isAllowed={({ formattedValue }) => isAllowedValue(formattedValue, fieldParameters)}
          name={formField.name}
          allowNegative={fieldParameters.allow_negative}
        />
      </InputGroup>
    </FormFieldControl>
  );
};
